import { Filmomtale } from '@filmweb/sanity-types';
import SMaxWidthMovieGrid, { SMaxWidthMovieGridHeader, SMovieCell } from 'components/moviegrid/SMaxWidthMovieGrid';
import MoviePoster from 'components/movieposter/MoviePoster';
import { endOfMonth, format, isBefore, isSameYear, parse } from "date-fns";
import locale from "date-fns/locale/nb";
import { useBaseTitle } from 'lib/client/useTitle';
import { setDefaultCacheHeaders } from "lib/server/cache";
import { fwpakkeQuery, getFilmwebFilmomtale, getServerData } from "lib/server/sanity";
import { ICommonAppData, IMovieArticle, IUpcomingPage } from "lib/types/sanity";
import { GetServerSideProps, GetServerSidePropsContext } from "next";
import dynamic from 'next/dynamic';
import Head from 'next/head';
import styles from "./Kommer.module.css";

const DynamicAdnuntiusAd = dynamic(() =>
	import('components/framework/AdnuntiusAd').then((mod) => mod.default),
	{ ssr: false }
);


//#region [Other]getServerSideProps
interface ISortableMovie {
	premiereLabel: string | null;
	localMovieArticle: IMovieArticle;
	filmwebMovie?: Filmomtale | null;
	sortableDate?: string;
}

/**
 *
 * @param d the date
 * @param uncertain is the date uncertain?
 * @returns a formatted date, or undefined if the date is passed
 */
function formatPremiereDate(d: string | Date, uncertain: boolean = false): string | null {
	let date: Date | undefined;
	if (typeof (d) === "string") {
		date = parse(d, "yyyy-MM-dd", new Date()); // we add a time to make sure the timezone does not fuck up the time
	} else {
		date = d;
	}

	// if the date is passed, just return
	if (isBefore(date, new Date())) {
		return null;
	}

	if (isSameYear(date as Date, new Date())) {
		return format(date as Date, uncertain ? "MMMM" : "d. MMMM", { locale });
	}
	return format(date as Date, uncertain ? "MMMM yyyy" : "dd.MM.yyyy", { locale });

}

export const getServerSideProps: GetServerSideProps = getServerData(async (context) => {
	setDefaultCacheHeaders(context as GetServerSidePropsContext);
	const pageData = await fwpakkeQuery<IUpcomingPage>(UPCOMING_PAGE_QUERY);
	let movies: ISortableMovie[] | undefined | null = pageData?.movies?.filter(movie => !!movie)?.map(movie => {
		const result: ISortableMovie = {
			localMovieArticle: movie,
			premiereLabel: null
		};
		if (movie.premiereDate) {
			result.sortableDate = movie.premiereDate;
			result.premiereLabel = formatPremiereDate(movie.premiereDate);
		}
		return result;
	});
	if ((movies?.length ?? 0) > 0) {
		const edis = movies!.map(m => m.localMovieArticle.edi.current);

		const fwMovieArticles = await getFilmwebFilmomtale(edis as string[]);

		const fwMovieArticleMap = fwMovieArticles?.reduce((previousValue: Record<string, Filmomtale>, currentValue) => {
			previousValue[currentValue.mainVersionEDI] = currentValue;
			return previousValue;
		}, {});

		if (fwMovieArticleMap) {
			movies!.forEach(m => {
				m.filmwebMovie = fwMovieArticleMap[m.localMovieArticle.edi.current] ?? null;
				if (!m.sortableDate) {
					if (!m.filmwebMovie) {
						m.filmwebMovie = null;
						m.premiereLabel = "Ukjent premiere";
						m.sortableDate = "2100-12-31";
						console.error("MISSING FROM FILMWEB" + m.localMovieArticle.edi.current + " " + m.localMovieArticle.title);
					} else {
						if (m.filmwebMovie.premiere?.premiereDate) {
							m.sortableDate = m.filmwebMovie.premiere!.premiereDate;
							m.premiereLabel = formatPremiereDate(m.filmwebMovie.premiere!.premiereDate);
						} else if (m.filmwebMovie.premiere?.expectedPremiereDate) {
							let tempDate = parse(m.filmwebMovie.premiere?.expectedPremiereDate, "yyyy-MM-dd", new Date(), { locale });
							tempDate = endOfMonth(tempDate);
							m.sortableDate = format(tempDate, "yyyy-MM-dd", { locale });
							m.premiereLabel = formatPremiereDate(tempDate, true);
						}
					}
				}
			});
			//movies = movies?.filter(m => typeof m.filmwebMovie !== "undefined");
			//movies = movies?.filter(m => !!m.filmwebMovie); // we need the filmweb article. (Should not happen that isn doesn't exist, but you know how it is)
			const refDate = new Date();

			movies?.sort((m1, m2) => {
				if (!m1.sortableDate) {
					return 1;
				}
				if (!m2.sortableDate) {
					return -1;
				}

				const m1d = parse(m1.sortableDate, "yyyy-MM-dd", refDate);
				const m2d = parse(m2.sortableDate, "yyyy-MM-dd", refDate);

				return m1d.getTime() - m2d.getTime();

			});

		} else {
			return { notFound: true };
		}
	}

	// remove passed movies
	movies = movies?.filter(movie => !!movie.premiereLabel);
	if (!movies) {
		movies = null;
	}
	return {
		props: {
			movies
		}, // will be passed to the page component as props

	}
});
//#endregion


//#region [Props]
type UpcomingProps = {
	appData: ICommonAppData;
	movies?: ISortableMovie[];
};
//#endregion

//#region [Component]
export default function Kommende({ movies, appData }: UpcomingProps) {
	const title = useBaseTitle(appData.settings, "Kommende filmer");
	return <SMaxWidthMovieGrid as="section" $columns={2}>
		<Head>
			<title>{title}</title>
			<meta property="og:title" content={title} key="title" />
		</Head>
		<SMaxWidthMovieGridHeader $columns={2}>{appData?.settings?.nynorsk ? "Komande filmar" : "Kommende filmer"}</SMaxWidthMovieGridHeader>

		{(movies?.length ?? 0) === 0 && <div>{appData?.settings?.nynorsk ? "Kinoen har ikkje lagt ut informasjon om kommande filmar akkurat no." : "Kinoen har ikke lagt ut informasjon om kommende filmer for øyeblikket."}</div>}
		{(movies?.length ?? 0) > 0 && <>
			<DynamicAdnuntiusAd className={styles.Ad} hideOnMobile={false} hideOnDesktop={true} appData={appData} index={1} />
			{movies!.map(movie => {
				return <SMovieCell key={movie.localMovieArticle.edi.current} > <MoviePoster edi={movie.localMovieArticle.edi.current} fallbackTitle={movie.localMovieArticle.titleOverride ?? movie.localMovieArticle.title} label={movie.premiereLabel!} filmomtale={movie.filmwebMovie} fwpakkeFilmomtale={movie.localMovieArticle} allowTitleOverride={appData.settings.canOverrideMovieTitles} /></SMovieCell>;
			})}
		</>}


	</SMaxWidthMovieGrid >
}
//#endregion

//#region [Other]
const UPCOMING_PAGE_QUERY = `*[_type == "fwpakkeUpcoming"][0] {
	movies[]->
}`;
//#endregion